import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

export default function ProcessStepsBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["processSteps"]>) {
  const onDark = isDarkBackground(settings);

  return (
    <SectionShell settings={settings}>
      <SectionHeading
        eyebrow={content.eyebrow}
        heading={content.heading}
        intro={content.intro}
        settings={settings}
      />

      {/* An ordered list, because the order is the meaning. */}
      <RevealList
        as="ol"
        className={cn(
          "grid gap-10",
          content.layout === "vertical" ? "max-w-2xl" : "md:grid-cols-2 lg:grid-cols-4",
        )}
      >
        {content.steps.map((step, index) => (
          <RevealItem key={step.title}>
            {/* The numeral carries the order, so it is an accent rather than a
                ghost: gold reads clearly on both grounds at this size. */}
            <span
              aria-hidden
              className={cn(
                "font-display text-5xl font-semibold tabular-nums",
                onDark ? "text-gold-400" : "text-gold-600",
              )}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3
              className={cn(
                "mt-3 font-display text-xl",
                onDark ? "text-cream-50" : "text-navy-900",
              )}
            >
              {step.title}
            </h3>
            {step.description && (
              <p
                className={cn(
                  "mt-2 text-sm leading-relaxed",
                  onDark ? "text-navy-200" : "text-ink-600",
                )}
              >
                {step.description}
              </p>
            )}
            {step.duration && (
              <p
                className={cn(
                  "mt-3 text-xs font-semibold uppercase tracking-wider",
                  onDark ? "text-gold-300" : "text-gold-700",
                )}
              >
                {step.duration}
              </p>
            )}
          </RevealItem>
        ))}
      </RevealList>
    </SectionShell>
  );
}
